import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Shield, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("admin_token", data.access_token);
        toast.success("Login successful");
        onLogin(data.access_token);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Login failed");
      }
    } catch (err) {
      toast.error("Login failed. Please try again.");
    }
    
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #09090b 0%, #18181b 50%, #09090b 100%)"
      }}
    >
      <div className="absolute inset-0 opacity-50" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>
      
      <div 
        className="absolute top-1/4 -left-32 w-96 h-96 rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)" }}
      ></div>
      <div 
        className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)" }}
      ></div>

      <Card 
        className="w-full max-w-md border-zinc-800 relative z-10"
        style={{ backgroundColor: 'rgba(24, 24, 27, 0.8)', backdropFilter: 'blur(10px)' }}
        data-testid="admin-login-card"
      >
        <CardHeader className="text-center space-y-4 pb-2">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-800 flex items-center justify-center" 
               style={{ boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)' }}>
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MedPlexity Admin
            </CardTitle>
            <CardDescription className="text-zinc-400 mt-1">
              Authentication Dashboard
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-300">Admin Username</Label>
              <Input
                id="username"
                data-testid="admin-username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                className="bg-zinc-950/50 border-zinc-800 focus:border-violet-500 focus:ring-violet-500/20 text-white placeholder:text-zinc-600"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="admin-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-zinc-950/50 border-zinc-800 focus:border-violet-500 focus:ring-violet-500/20 text-white placeholder:text-zinc-600 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold py-5 transition-all duration-300"
              style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)' }}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
                  Authenticating...
                </span>
              ) : (
                "Access Dashboard"
              )}
            </Button>
          </form>
          
          <p className="text-center text-xs text-zinc-600 mt-6">
            Internal Admin Access Only
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLoginPage;
